import LegalDocShell from "@/components/legal/LegalDocShell";
import { buildLegalSections } from "@/lib/legalRenderer";

const SECTIONS = buildLegalSections([
  { id: "philosophy", title: "1. Our Community Philosophy", body:
`NaturePulse begins with something very simple: Observe what is happening around you.

A flower blooming earlier than usual. A bird building a nest. A farmer noticing a new insect. A stream running unusually low. A tree producing fruit. A field damaged by unexpected rain. A butterfly appearing for the first time that season. A sunset, cloud formation, drought, flood, harvest, forest, animal, or changing landscape.

Every observation can contribute to a larger understanding of nature. We encourage our community to: Observe • Document • Learn • Share • Respect • Protect`
  },
  { id: "respectful", title: "2. Be Respectful", body:
`NaturePulse is intended for people from different countries, cultures, languages, professions, educational backgrounds, and levels of expertise.

Treat others with respect. Healthy disagreement is welcome. Personal attacks are not.

You may respectfully challenge:
- A species identification
- A farming technique
- A scientific interpretation
- An environmental claim
- A photograph description
- An agricultural recommendation
- Another user's conclusion

Focus your disagreement on the information, not the individual.`
  },
  { id: "welcoming", title: "3. Help Us Build a Welcoming Community", body:
`We want NaturePulse to be welcoming to beginners as well as experts. Someone who cannot identify a bird, insect, plant, or crop should feel comfortable asking for help.

Do not ridicule users for:
- Asking basic questions
- Making an incorrect identification
- Misspelling scientific names
- Having limited agricultural knowledge
- Speaking a different language
- Being unfamiliar with technology
- Making a good-faith mistake

Correct information respectfully. Knowledge grows when it is shared generously.`
  },
  { id: "genuine", title: "4. Share Genuine Observations", body:
`NaturePulse depends upon trustworthy observations. When reporting something you personally observed, please try to provide accurate information. Whenever possible, include:
- Photograph or video
- Observation date
- Approximate location
- Description
- Species or crop, if known
- Weather or environmental conditions
- Relevant tags
- Notes explaining what you observed

If you are uncertain about an identification, say so. There is nothing wrong with: "Unknown species — can someone help identify this?" That is preferable to presenting an uncertain identification as a fact.`
  },
  { id: "no-fabrication", title: "5. Do Not Fabricate Observations", body:
`Do not intentionally submit false environmental or agricultural observations. Examples include intentionally reporting:
- A species that was not present
- Rainfall that did not occur
- A nonexistent pest outbreak
- A fabricated crop disease
- False flooding
- False wildfire information
- Fake harvest information
- Manipulated agricultural prices
- A false wildlife sighting
- An observation at a location where it did not occur

Good-faith mistakes are understandable. Deliberate manipulation of observation data undermines the value of the entire NaturePulse community.`
  },
  { id: "observation-opinion", title: "6. Clearly Distinguish Observation From Opinion", body:
`Whenever possible, distinguish between what you observed and what you think it means. For example:

## Observation
"Several tomato leaves developed brown spots during the past five days."

## Interpretation
"I think this may be a fungal disease."

This distinction is particularly important for citizen science and Farmers Pulse. Other users, experts, or AI tools may suggest alternative explanations.`
  },
  { id: "ai-suggestions", title: "7. AI Identifications Are Suggestions", body:
`NaturePulse may use artificial intelligence to help identify plants, trees, flowers, birds, animals, insects, fish, reptiles, crops, weeds, pests, possible plant diseases, environmental conditions, and other observations.

AI can make mistakes. An AI identification should therefore be treated as an assisted identification, not an unquestionable fact.

Community members are encouraged to confirm, correct, or improve AI-generated identifications. Where available, NaturePulse may display confidence levels or verification status.`
  },
  { id: "not-food-safe", title: "8. Never Use NaturePulse Identification as Proof Something Is Safe to Eat", body:
`This rule is especially important. Do not eat a mushroom, wild plant, berry, fruit, herb, seed, nut, fish, shellfish, insect, or other organism solely because NaturePulse, another user, or an AI system identifies it as edible.

Misidentification can cause serious injury or death. Always consult appropriate authoritative sources or qualified experts before consuming unfamiliar wild organisms.`
  },
  { id: "wildlife-respect", title: "9. Respect Wildlife", body:
`NaturePulse encourages observation—not interference. When photographing or observing wildlife:
- Maintain a safe distance
- Avoid disturbing nests
- Do not unnecessarily handle wildlife
- Do not chase animals for photographs
- Do not intentionally frighten wildlife
- Do not remove eggs or young animals
- Do not damage habitat
- Do not feed wildlife where inappropriate or prohibited
- Follow local wildlife laws

Getting a photograph is never more important than protecting the animal.`
  },
  { id: "sensitive-species", title: "10. Protect Sensitive Species", body:
`Some observations should not reveal exact locations. This may include:
- Endangered species
- Threatened species
- Rare plants
- Nesting locations
- Breeding sites
- Valuable orchids
- Rare reptiles
- Wildlife vulnerable to poaching
- Sensitive marine species
- Protected habitats

NaturePulse may automatically obscure or generalize locations associated with sensitive species. Users should never deliberately expose a sensitive location when doing so could place a species or habitat at risk.`
  },
  { id: "no-poaching", title: "11. No Poaching or Illegal Wildlife Trade", body:
`NaturePulse must never be used to facilitate:
- Poaching
- Illegal hunting
- Illegal fishing
- Wildlife trafficking
- Illegal plant collection
- Sale of protected species
- Collection of protected eggs
- Destruction of protected habitats
- Other illegal exploitation of nature

Content intended to facilitate these activities may be removed and accounts may be restricted or terminated.`
  },
  { id: "private-property", title: "12. Respect Private Property", body:
`A location appearing on a NaturePulse map does not mean you have permission to enter it. Do not trespass on:
- Farms
- Fields
- Gardens
- Private forests
- Residential property
- Restricted government land
- Conservation areas
- Indigenous or tribal lands
- Research areas
- Other restricted property

Obtain permission where required.`
  },
  { id: "photo-privacy", title: "13. Photography and Privacy", body:
`NaturePulse is highly visual, but please respect other people's privacy. Before posting photographs, consider whether they contain:
- Clearly identifiable individuals
- Children
- Private homes
- Vehicle license plates
- Personal documents
- Farm security information
- Private property details
- Other sensitive information

Whenever appropriate, obtain permission from identifiable individuals before making their image public. Exercise particular care with photographs of children.`
  },
  { id: "farmers-community", title: "14. Farmers Pulse Community", body:
`Farmers Pulse is intended to create a worldwide community where farmers, growers, gardeners, agricultural experts, researchers, and communities can learn from one another.

Farmers are encouraged to share observations concerning crops, planting, harvesting, flowering, soil, rainfall, irrigation, drought, flooding, pests, diseases, weeds, pollinators, livestock, poultry, farm practices, agricultural technology, harvest results, local conditions, and traditional farming knowledge.

Farmers Pulse should be a place where practical experience and scientific knowledge can complement one another.`
  },
  { id: "farmer-knowledge", title: "15. Respect Farmers' Knowledge", body:
`Farmers often possess valuable knowledge accumulated through generations of experience.

Scientific research is valuable. Traditional agricultural knowledge is also valuable. Community members should be able to discuss both respectfully.

Claims presented as scientific facts should, whenever practical, be supported by credible evidence. Traditional practices should be identified appropriately rather than automatically presented as scientifically proven.`
  },
  { id: "advice", title: "16. Agricultural Advice", body:
`Advice shared through Farmers Pulse should be treated as general information unless it comes from an appropriately qualified professional acting within their expertise.

Agricultural conditions vary greatly according to country, region, climate, soil, crop, variety, season, water availability, regulations, and farming practices. What works on one farm may not work on another.`
  },
  { id: "pesticides", title: "17. Pesticides and Agricultural Chemicals", body:
`Exercise particular caution when discussing pesticides, herbicides, fungicides, fertilizers, veterinary medications, agricultural chemicals, and other regulated products.

Do not encourage illegal, dangerous, off-label, or irresponsible use. Users must follow applicable laws, official product labels, safety instructions, waiting periods, protective-equipment requirements, and local agricultural regulations.

Where appropriate, consult qualified agricultural professionals.`
  },
  { id: "pest-reports", title: "18. Report Pests and Diseases Responsibly", body:
`Farmers Pulse may help communities identify emerging agricultural problems. When reporting a possible pest or disease:
- Provide photographs when possible
- Identify the crop
- Provide an approximate location
- Include the date
- Describe symptoms
- Indicate whether identification is confirmed or suspected
- Avoid presenting speculation as confirmed diagnosis

Reports of serious regulated agricultural diseases or invasive species may need to be reported to appropriate government agricultural authorities. NaturePulse is not a substitute for official reporting requirements.`
  },
  { id: "weather", title: "19. Weather and Emergency Information", body:
`Community weather observations can be extremely valuable. However, NaturePulse is not an emergency warning service. Do not rely exclusively on NaturePulse for decisions involving:
- Severe storms
- Flooding
- Wildfires
- Hurricanes
- Tornadoes
- Lightning
- Extreme heat
- Extreme cold
- Evacuations
- Other emergencies

Follow official emergency and weather authorities for safety-critical information.`
  },
  { id: "market", title: "20. Agricultural Market Information", body:
`Farmers may share local observations about produce prices, commodity prices, input costs, buyer demand, harvest availability, and local markets.

Do not deliberately submit false prices or manipulate market information. Market information should include a date and location whenever practical because agricultural prices can change quickly.

NaturePulse does not guarantee prices reported by users.`
  },
  { id: "no-spam", title: "21. No Spam", body:
`Do not use NaturePulse to repeatedly post:
- Unrelated advertisements
- Promotional links
- Referral spam
- Duplicate posts
- Misleading offers
- Unsolicited commercial messages
- Automated comments
- Other disruptive content

Businesses and organizations may participate where permitted, but commercial participation must follow applicable NaturePulse rules.`
  },
  { id: "external-links", title: "22. External Links", body:
`You may share useful external resources when relevant. Examples include research papers, government agricultural information, university resources, educational videos, conservation organizations, weather information, scientific publications, farming resources, and relevant news articles.

When sharing another person's content, provide appropriate attribution and link to the original source whenever possible. Do not copy entire copyrighted articles into NaturePulse. A short description followed by a link to the original source is generally preferable.`
  },
  { id: "copyright-respect", title: "23. Respect Copyright", body:
`Only upload photographs, videos, articles, illustrations, or other material that you created, you have permission to use, is appropriately licensed, or you otherwise have a lawful right to share.

Giving credit does not automatically give you permission to reproduce copyrighted material. When sharing third-party information, link to the original source whenever appropriate.`
  },
  { id: "no-harassment", title: "24. No Harassment or Threats", body:
`NaturePulse does not permit harassment, intimidation, stalking, or credible threats. Do not target users because of personal characteristics or repeatedly contact someone who has clearly asked you to stop.

Disagreement is acceptable. Abuse is not.`
  },
  { id: "no-hate", title: "25. No Hate or Dehumanizing Content", body:
`NaturePulse is an international community. Content attacking or dehumanizing people based on protected characteristics is not permitted.

NaturePulse should remain a place where people from different communities can participate safely and respectfully.`
  },
  { id: "protect-personal", title: "26. Protect Personal Information", body:
`Do not publicly post another person's private information without authorization. This includes information such as:
- Home addresses
- Personal telephone numbers
- Private email addresses
- Financial information
- Identification documents
- Passwords
- Medical information
- Private GPS coordinates
- Other sensitive personal information

Users should also exercise caution when posting their own personal information publicly.`
  },
  { id: "protect-farm", title: "27. Protect Farm Locations", body:
`Farmers Pulse participants should carefully consider whether to publish exact farm or field locations. Precise agricultural locations could reveal:
- Valuable crops
- Equipment
- Livestock
- Water sources
- Farm infrastructure
- Commercially sensitive information
- Private residences

Where available, farmers may choose to display an approximate area rather than precise GPS coordinates.`
  },
  { id: "students", title: "28. Children and Students", body:
`Students can make valuable contributions to citizen science. However, children's privacy and safety require special protection.

Do not publicly disclose unnecessary information about minors, including:
- Exact home location
- School schedules
- Personal contact information
- Private photographs without appropriate authorization
- Other information that could create safety or privacy risks

Schools, teachers, parents, and guardians should follow applicable consent requirements.`
  },
  { id: "corrections", title: "29. Community Corrections", body:
`NaturePulse should encourage collaborative verification. If you believe an observation is incorrectly identified, provide a respectful correction. Whenever possible, explain why. For example:

"I believe this may be a Monarch butterfly rather than a Viceroy because..."

This is much more helpful than: "Wrong."

The objective is not to prove who knows more. The objective is to improve the collective record.`
  },
  { id: "verification-levels", title: "30. Verification and Confidence Levels", body:
`NaturePulse may eventually use indicators such as:
- Unverified
- AI Suggested
- Community Suggested
- Community Verified
- Expert Verified
- Research Grade

Such labels indicate the level of available verification and should not be interpreted as absolute guarantees of accuracy. Observations may be corrected as better information becomes available.`
  },
  { id: "reporting", title: "31. Reporting Content", body:
`If you encounter content that appears to violate these Guidelines, use the available Report feature. Possible report categories may include:
- Incorrect information
- Spam
- Harassment
- Copyright violation
- Privacy violation
- Dangerous agricultural advice
- Sensitive wildlife location
- Illegal wildlife activity
- Graphic content
- Fraud
- False observation
- Inappropriate content
- Other concern

Please provide enough information for moderators to understand the issue.`
  },
  { id: "moderation", title: "32. Moderation", body:
`NaturePulse may review content that is reported or detected by moderation systems. Depending upon the circumstances, we may:
- Leave the content unchanged
- Add a warning
- Add context
- Correct categorization
- Reduce location precision
- Restrict visibility
- Remove content
- Limit posting privileges
- Temporarily suspend an account
- Terminate an account

NaturePulse may use a combination of automated systems, AI, community reports, moderators, and subject-matter experts.`
  },
  { id: "good-faith", title: "33. Good-Faith Mistakes", body:
`Citizen science depends upon participation. People will make mistakes.

A user who accidentally identifies the wrong flower, bird, insect, crop, or disease should generally be helped—not punished.

NaturePulse distinguishes between good-faith mistakes and deliberate deception or repeated abuse. We encourage correction, education, and collaboration whenever appropriate.`
  },
  { id: "experts", title: "34. Community Experts", body:
`NaturePulse may recognize experienced contributors, farmers, researchers, scientists, agricultural specialists, educators, or other experts.

An expert badge or designation does not mean NaturePulse guarantees everything that person says. Users should continue to evaluate important information independently.`
  },
  { id: "researchers", title: "35. Researchers and Organizations", body:
`Researchers, universities, government agencies, conservation organizations, agricultural institutions, and nonprofits are welcome to participate where appropriate. We encourage organizations to:
- Clearly identify themselves
- Explain research projects
- Respect contributor privacy
- Provide attribution where appropriate
- Follow applicable research ethics
- Avoid misleading recruitment
- Follow NaturePulse data-use requirements`
  },
  { id: "integrity", title: "36. Protect the Integrity of NaturePulse Data", body:
`NaturePulse observations may eventually contribute to research and historical environmental records. Therefore, do not attempt to manipulate the platform through:
- Fake accounts
- Fabricated observations
- Coordinated false reporting
- Automated spam
- Location falsification
- Deliberately altered evidence
- Misleading AI-generated observations
- Other methods intended to corrupt NaturePulse data`
  },
  { id: "ai-images", title: "37. AI-Generated Images and Synthetic Content", body:
`Do not upload an AI-generated image and represent it as a photograph of an actual natural or agricultural observation.

Synthetic or AI-generated media used for educational or illustrative purposes should be clearly identified as such. For example: "AI-generated illustration — not an actual observation."

Protecting the distinction between documented reality and generated imagery is essential to NaturePulse's mission.`
  },
  { id: "commercial", title: "38. Commercial Participation", body:
`Farmers, nurseries, agricultural businesses, conservation organizations, photographers, tourism organizations, and other businesses may participate where permitted.

Commercial users must not disguise advertising as independent observations. Sponsored or promotional content should be clearly identified when required.

NaturePulse may establish additional business, marketplace, or promotional guidelines as these services develop.`
  },
  { id: "repeated", title: "39. Repeated Violations", body:
`Users who repeatedly violate these Guidelines may have:
- Content removed
- Posting privileges restricted
- Features disabled
- Accounts suspended
- Accounts terminated

Serious violations may result in immediate action.`
  },
  { id: "appeals", title: "40. Appeals", body:
`Where appropriate, NaturePulse may provide users with a method to request review of moderation decisions. An appeal does not guarantee that content or an account will be restored.

We will consider available information and applicable policies when reviewing appeals.`
  },
  { id: "changes", title: "41. These Guidelines Will Evolve", body:
`NaturePulse is designed to grow into a worldwide community. New technologies, AI capabilities, agricultural services, research programs, and community features may require additional rules.

We may therefore update these Community Guidelines periodically. Significant changes may be communicated through NaturePulse.net or other appropriate methods.`
  },
  { id: "contact", title: "42. Contact NaturePulse", body:
`Questions about these Community Guidelines may be directed to:

NaturePulse.net, a division of Social Pulse Inc.
Website: [NaturePulse.net](https://naturepulse.net)
Community Support: contact@naturepulse.net
Privacy: legal@naturepulse.net
Copyright: support@naturepulse.net

Mailing Address:
Social Pulse Inc.
15166 SE Aston Loop
Portland, OR 97236
United States`
  },
]);

export default function CommunityGuidelines() {
  return (
    <LegalDocShell
      eyebrow="NaturePulse · Community Guidelines"
      title="Community Guidelines"
      effective="August 23, 2026"
      updated="August 23, 2026"
      intro={
        <>
          <p>NaturePulse.net, a division of Social Pulse Inc. ("NaturePulse," "we," "us," or "our") is a global community for people who want to observe, document, understand, and share the changing pulse of our living planet.</p>
          <p>NaturePulse brings together nature enthusiasts, photographers, farmers, gardeners, students, educators, scientists, researchers, conservationists, travelers, local communities, and anyone interested in the natural world. Our specialized Farmers Pulse community extends this mission to agriculture.</p>
          <p>These Community Guidelines are intended to keep NaturePulse useful, respectful, trustworthy, safe, and welcoming. By participating in NaturePulse or Farmers Pulse, you agree to follow these Guidelines together with our <a href="/legal/terms">Terms of Service</a> and Privacy Policy.</p>
        </>
      }
      sections={SECTIONS}
    />
  );
}
